import { ReactNode, forwardRef } from "react";
import { cn } from "../utils";
import "../styles/index.css";

export interface FooterProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Footer content
   */
  children: ReactNode;
  /**
   * Enable glassmorphism effect
   * @default false
   */
  glass?: boolean;
  /**
   * Show ecosystem navigation
   * @default true
   */
  showEcosystem?: boolean;
  /**
   * Current domain to highlight in ecosystem nav (e.g., "lukhas.ai", "lukhas.cloud")
   */
  activeDomain?: string;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Footer - LUKHAS design system footer component
 *
 * Features:
 * - Unified ecosystem navigation
 * - Constellation Framework branding
 * - Optional glassmorphism
 * - Responsive layout
 *
 * @example
 * ```tsx
 * <Footer showEcosystem>
 *   <FooterSection title="Product">
 *     <FooterLink href="/features">Features</FooterLink>
 *   </FooterSection>
 * </Footer>
 * ```
 */
export const Footer = forwardRef<HTMLElement, FooterProps>(
  (
    { children, glass = false, showEcosystem = true, activeDomain, className, ...props },
    ref,
  ) => {
    return (
      <footer
        ref={ref}
        className={cn(
          "w-full border-t mt-auto",
          glass && "bg-glass backdrop-blur-glass border-glass-border",
          !glass && "bg-deep-space border-border",
          className,
        )}
        {...props}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {showEcosystem && (
            <div className="mb-12 pb-8 border-b border-border">
              <FooterEcosystemNav activeDomain={activeDomain} />
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            {children}
          </div>

          <FooterBottom />
        </div>
      </footer>
    );
  },
);

Footer.displayName = "Footer";

// Dev mode port mapping for local development
const devPortMap: Record<string, number> = {
  "lukhas.ai": 3004,
  "lukhas.id": 3001,
  "lukhas.com": 3002,
  "lukhas.us": 3003,
  "lukhas.cloud": 3005,
  "lukhas.dev": 3006,
  "lukhas.io": 3007,
  "lukhas.eu": 3008,
  "lukhas.app": 3010,
  "lukhas.store": 3011,
  "lukhas.xyz": 3012,
  "lukhas.team": 3013,
};

/**
 * Get the appropriate href for an ecosystem link
 * In dev mode (localhost), returns localhost URL with correct port
 * In production, returns the production URL
 */
const getEcosystemHref = (domain: string): string => {
  const isDev = typeof window !== "undefined" && window.location.hostname === "localhost";
  if (isDev) {
    const port = devPortMap[domain];
    return port ? `http://localhost:${port}` : `https://${domain}`;
  }
  return `https://${domain}`;
};

/**
 * FooterEcosystemNav - Navigation across LUKHAS domains using Λ prefix format
 *
 * All ecosystem links with elegant hover effects
 */
export const FooterEcosystemNav = ({ activeDomain }: { activeDomain?: string }) => {
  const ecosystemLinks = [
    { domain: "lukhas.ai", label: "I", description: "Lukhas AI Platform" },
    { domain: "lukhas.com", label: "", description: "Lukhas Corporate" },
    { domain: "lukhas.dev", label: "Dev", description: "Lukhas Developers" },
    { domain: "lukhas.io", label: "iO", description: "Lukhas I/O" },
    { domain: "lukhas.id", label: "iD", description: "Lukhas Identity" },
    { domain: "lukhas.cloud", label: "Cloud", description: "Lukhas Cloud" },
    { domain: "lukhas.app", label: "App", description: "Lukhas App" },
    { domain: "lukhas.us", label: "US", description: "Lukhas US" },
    { domain: "lukhas.eu", label: "EU", description: "Lukhas EU" },
    { domain: "lukhas.store", label: "Store", description: "Lukhas Store" },
    { domain: "lukhas.xyz", label: "XYZ", description: "Lukhas XYZ" },
  ];

  return (
    <div className="flex flex-wrap justify-between items-center gap-y-4">
      {ecosystemLinks.map((item) => {
        const href = getEcosystemHref(item.domain);
        const isActive = activeDomain === item.domain;
        return (
          <a
            key={item.domain}
            href={href}
            aria-label={item.description}
            title={item.description}
            className={cn(
              "text-base font-light tracking-[0.15em] transition-all duration-500 ease-out",
              "hover:scale-150 hover:text-white",
              isActive
                ? "text-lambda-blue scale-110"
                : "text-text-secondary/40 hover:text-white",
            )}
            style={{
              filter: isActive
                ? "drop-shadow(0 0 10px rgba(56, 189, 248, 0.5))"
                : undefined,
              textShadow: isActive
                ? "0 0 20px rgba(56, 189, 248, 0.5)"
                : undefined,
            }}
          >
            Λ{item.label && <span className="text-[10px] align-top">{item.label}</span>}
          </a>
        );
      })}
    </div>
  );
};

/**
 * FooterSection - Footer column section
 */
export interface FooterSectionProps
  extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  children: ReactNode;
  className?: string;
}

export const FooterSection = forwardRef<HTMLDivElement, FooterSectionProps>(
  ({ title, children, className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("flex flex-col gap-3", className)}
        {...props}
      >
        <h4 className="text-sm font-light uppercase tracking-logo text-text-primary mb-2">
          {title}
        </h4>
        <div className="flex flex-col gap-2">{children}</div>
      </div>
    );
  },
);

FooterSection.displayName = "FooterSection";

/**
 * FooterLink - Footer navigation link
 */
export interface FooterLinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  children: ReactNode;
  className?: string;
}

export const FooterLink = forwardRef<HTMLAnchorElement, FooterLinkProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <a
        ref={ref}
        className={cn(
          "text-sm text-text-secondary hover:text-lambda-blue transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lambda-blue focus-visible:rounded-sm",
          className,
        )}
        {...props}
      >
        {children}
      </a>
    );
  },
);

FooterLink.displayName = "FooterLink";

/**
 * FooterBottom - Footer bottom with copyright and legal
 */
export const FooterBottom = () => {
  const currentYear = new Date().getFullYear();

  return (
    <div className="pt-8 border-t border-border flex flex-col sm:flex-row justify-between items-center gap-4">
      <div className="text-xs text-text-secondary font-light tracking-wide">
        Logic Unified Knowledge Hyper Adaptable Systems © {currentYear}
      </div>

      <div className="flex items-center gap-4">
        <a
          href="/privacy"
          className="text-xs text-text-secondary hover:text-lambda-blue transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lambda-blue focus-visible:rounded-sm"
        >
          Privacy Policy
        </a>
        <a
          href="/terms"
          className="text-xs text-text-secondary hover:text-lambda-blue transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lambda-blue focus-visible:rounded-sm"
        >
          Terms of Service
        </a>
        <a
          href="/security"
          className="text-xs text-text-secondary hover:text-lambda-blue transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lambda-blue focus-visible:rounded-sm"
        >
          Security
        </a>
      </div>
    </div>
  );
};

FooterBottom.displayName = "FooterBottom";
